import type { Tables } from '@/integrations/supabase/types';
import { getBillingSummary } from '@/lib/billing-display';

type Program = Tables<'programs'>;

export type CartPaymentOption = 'full' | 'deposit' | 'payment_plan' | 'recurring' | 'payg';

export interface CartPaymentOptionInfo {
  value: CartPaymentOption;
  label: string;
  hint: string;
  /** Amount charged/scheduled per child for display purposes. null = varies. */
  perChildAmount: number | null;
  /** Amount actually charged at checkout per child. null = varies. */
  dueTodayPerChild: number | null;
  /** Short sentence describing what is charged later and when. */
  laterNote?: string;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Display-only list of the payment options a program offers. Mirrors the
 * richer logic in CheckoutProgramsStep, kept lightweight for the cart.
 */
export function getCartPaymentOptions(program: Program): CartPaymentOptionInfo[] {
  const p = program as any;
  const options: Omit<CartPaymentOptionInfo, 'dueTodayPerChild' | 'laterNote'>[] = [];

  if (p.full_session_enabled && program.full_session_price) {
    options.push({
      value: 'full',
      label: 'Pay in Full',
      hint: 'One payment for the whole session',
      perChildAmount: program.full_session_price,
    });

    if (program.deposit_required && program.deposit_amount) {
      options.push({
        value: 'deposit',
        label: 'Pay Deposit',
        hint: 'Deposit now, balance before the start date',
        perChildAmount: program.deposit_amount,
      });
    }

    if (program.payment_plan_enabled && program.payment_plan_installments) {
      const amounts = program.payment_plan_installment_amounts as number[] | null;
      const first = amounts?.[0] ?? program.full_session_price / program.payment_plan_installments;
      options.push({
        value: 'payment_plan',
        label: `${program.payment_plan_installments}-Payment Plan`,
        hint: `Split into ${program.payment_plan_installments} installments`,
        perChildAmount: first,
      });
    }
  }

  // Session-based camps: price comes from the sessions chosen at checkout
  if ((p.sessions_enabled as boolean) && options.length === 0) {
    options.push({
      value: 'full',
      label: 'Pay in Full',
      hint: 'Priced by the camp sessions you choose',
      perChildAmount: null,
    });
  }

  if (program.monthly_subscription && program.price) {
    options.push({
      value: 'recurring',
      label: 'Monthly',
      hint: 'Automatic monthly billing',
      perChildAmount: program.price,
    });
  }

  if (program.pay_as_you_go) {
    const day = program.payg_billing_day || 1;
    options.push({
      value: 'payg',
      label: 'Pay As You Go',
      hint: `Billed by attendance on the ${day}${ordinal(day)} of each month`,
      perChildAmount: null,
    });
  }

  return options.map(o => {
    const summary = getBillingSummary(program, o.value);
    return {
      ...o,
      dueTodayPerChild: o.value === 'payg' ? 0 : summary.dueToday,
      laterNote: summary.detail,
    };
  });
}

/** Per-child amount charged at checkout for a cart line. null when pending. */
export function getCartLineDueToday(
  program: Program,
  option: CartPaymentOption | undefined,
): number | null {
  const available = getCartPaymentOptions(program);
  const effective = option ?? (available.length === 1 ? available[0].value : undefined);
  if (!effective) return null;
  return available.find(o => o.value === effective)?.dueTodayPerChild ?? null;
}

/**
 * The amount a cart line contributes to the cart subtotal, per child.
 * Returns null when the choice is still pending or varies (PAYG).
 */
export function getCartLineAmount(
  program: Program,
  option: CartPaymentOption | undefined,
): number | null {
  const available = getCartPaymentOptions(program);
  const effective = option ?? (available.length === 1 ? available[0].value : undefined);
  if (!effective) return null;
  const match = available.find(o => o.value === effective);
  if (!match) return null;
  return match.perChildAmount;
}
